/*
 * SKRS Stack Progress — native bulk-discount progress widget.
 *
 * Two custom elements:
 *   <skrs-stack-data>     "brain": the count is computed server-side in Liquid
 *                         (tag-aware; the AJAX cart JSON has no product tags).
 *                         It hooks the cart pipeline and broadcasts an integer.
 *   <skrs-stack-progress> "visual": renders tier / fill / message / pulse from
 *                         that integer. Server-side data-count is the initial
 *                         state, so every surface is correct on first paint.
 *
 * Events (dispatched by the theme on document.documentElement, bubbling):
 *   cart:prepare-bundled-sections  → detail.sections[] : request our section HTML
 *   cart:change                    → detail.cart.sections[id] : new section HTML
 *   cart:refresh                   → re-fetch via Section Rendering API
 * Internal:
 *   skrs:stack:update              → detail.count : the new integer count
 */
(function () {
  'use strict';

  // Pull the integer out of a server-rendered <skrs-stack-data data-count="N">.
  function parseCountFromHTML(html) {
    if (!html) return null;
    var m = html.match(/<skrs-stack-data[^>]*\bdata-count\s*=\s*["']?(\d+)/i);
    return m ? parseInt(m[1], 10) : null;
  }

  function toInt(v) {
    var n = parseInt(v, 10);
    return isNaN(n) ? 0 : n;
  }

  /* ===================== Brain ===================== */
  class SkrsStackData extends HTMLElement {
    connectedCallback() {
      var sec = this.closest('.shopify-section');
      this._sectionId =
        (sec && sec.id ? sec.id.replace(/^shopify-section-/, '') : '') ||
        this.getAttribute('data-section-id') ||
        'skrs-stack-brain';

      this._onBundle = this._onBundle.bind(this);
      this._onChange = this._onChange.bind(this);
      this._onRefresh = this._onRefresh.bind(this);

      document.addEventListener('cart:prepare-bundled-sections', this._onBundle);
      document.addEventListener('cart:change', this._onChange);
      document.addEventListener('cart:refresh', this._onRefresh);

      // Broadcast the server-rendered count once on connect.
      this._broadcast(toInt(this.getAttribute('data-count')));
    }

    disconnectedCallback() {
      document.removeEventListener('cart:prepare-bundled-sections', this._onBundle);
      document.removeEventListener('cart:change', this._onChange);
      document.removeEventListener('cart:refresh', this._onRefresh);
    }

    // Ask the cart pipeline to bundle our section HTML into the next change.
    _onBundle(e) {
      var sections = e && e.detail && e.detail.sections;
      if (Array.isArray(sections) && sections.indexOf(this._sectionId) === -1) {
        sections.push(this._sectionId);
      }
    }

    _onChange(e) {
      var html =
        e && e.detail && e.detail.cart && e.detail.cart.sections
          ? e.detail.cart.sections[this._sectionId]
          : null;
      var count = parseCountFromHTML(html);
      if (count !== null) {
        this.setAttribute('data-count', count);
        this._broadcast(count);
      } else {
        // Our section wasn't bundled into this change — fetch it directly.
        this._fetchAndBroadcast();
      }
    }

    _onRefresh() {
      this._fetchAndBroadcast();
    }

    _fetchAndBroadcast() {
      var self = this;
      var root =
        window.Shopify && Shopify.routes && Shopify.routes.root
          ? Shopify.routes.root
          : '/';
      fetch(root + '?section_id=' + encodeURIComponent(this._sectionId), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var count = parseCountFromHTML(html);
          if (count === null) count = 0;
          self.setAttribute('data-count', count);
          self._broadcast(count);
        })
        .catch(function () { /* keep last known state on network error */ });
    }

    _broadcast(count) {
      document.dispatchEvent(
        new CustomEvent('skrs:stack:update', { detail: { count: count } })
      );
    }
  }

  /* ===================== Visual ===================== */
  class SkrsStackProgress extends HTMLElement {
    connectedCallback() {
      this._msgEl = this.querySelector('.skrs-stack-progress__msg');
      this._fillEl = this.querySelector('.skrs-stack-progress__fill');
      this._discountEl = this.querySelector('[data-skrs-discount]');
      this._tiers = this._readTiers();

      this._onUpdate = this._onUpdate.bind(this);
      document.addEventListener('skrs:stack:update', this._onUpdate);

      // Initial render from server-side data-count.
      this._render(toInt(this.getAttribute('data-count')));
    }

    disconnectedCallback() {
      document.removeEventListener('skrs:stack:update', this._onUpdate);
    }

    _readTiers() {
      var d = this.dataset;
      return [
        { qty: toInt(d.t1Qty), label: d.t1Label || '' },
        { qty: toInt(d.t2Qty), label: d.t2Label || '' },
        { qty: toInt(d.t3Qty), label: d.t3Label || '' }
      ];
    }

    _onUpdate(e) {
      if (e && e.detail && typeof e.detail.count === 'number') {
        this.setAttribute('data-count', e.detail.count);
        this._render(e.detail.count);
      }
    }

    _fillTemplate(tpl, vars) {
      if (!tpl) return '';
      return tpl.replace(/\{(\w+)\}/g, function (whole, key) {
        return vars[key] !== undefined && vars[key] !== null ? vars[key] : '';
      });
    }

    _render(count) {
      var tiers = this._tiers;
      var max = tiers[2].qty || 1;

      if (count < 1) {
        this.hidden = true;
        this._setAnnouncement(false);
        return;
      }
      this.hidden = false;
      this._setAnnouncement(true);

      // Current tier index (-1 = stack started but no tier reached yet).
      var curIdx = -1;
      for (var i = 0; i < tiers.length; i++) {
        if (count >= tiers[i].qty) curIdx = i;
      }

      var pct = Math.max(0, Math.min(100, (count / max) * 100));
      if (this._fillEl) this._fillEl.style.width = pct + '%';

      var isMax = curIdx >= tiers.length - 1;
      this.classList.toggle('is-max', isMax);

      if (this._discountEl) {
        this._discountEl.textContent =
          curIdx >= 0 ? tiers[curIdx].label : (this.dataset.zeroLabel || '0% OFF');
      }

      var d = this.dataset;
      var msg;
      if (isMax) {
        msg = this._fillTemplate(d.copyMax, {
          max_label: tiers[tiers.length - 1].label,
          current_label: curIdx >= 0 ? tiers[curIdx].label : '',
          n: count
        });
      } else {
        var nextTier = tiers[curIdx + 1];
        var needed = Math.max(0, nextTier.qty - count);
        var vars = {
          needed: needed,
          next_label: nextTier.label,
          current_label: curIdx >= 0 ? tiers[curIdx].label : '',
          max_label: tiers[tiers.length - 1].label,
          n: count
        };
        msg = this._fillTemplate(curIdx < 0 ? d.copyCountdown : d.copyIntermediate, vars);
      }
      if (this._msgEl) this._msgEl.textContent = msg;

      this._maybePulse(curIdx);
    }

    // Gold pulse on crossing UP into a new tier. Last-seen tier is persisted in
    // sessionStorage (keyed by context) so re-renders — the cart drawer/page
    // destroy & recreate this element — neither replay nor miss the pulse.
    _pulseKey() {
      return 'skrs:stack:lastTier:' + (this.dataset.context || 'default');
    }

    _maybePulse(curIdx) {
      var prevIdx = -1;
      try {
        var stored = sessionStorage.getItem(this._pulseKey());
        if (stored !== null) prevIdx = toInt(stored);
      } catch (err) { /* sessionStorage unavailable */ }

      if (curIdx > prevIdx && curIdx >= 0) {
        var self = this;
        this.classList.remove('is-tier-up');
        void this.offsetWidth; // reflow so the animation can re-trigger
        this.classList.add('is-tier-up');
        window.setTimeout(function () {
          self.classList.remove('is-tier-up');
        }, 700);
      }

      try { sessionStorage.setItem(this._pulseKey(), String(curIdx)); } catch (err) {}
    }

    // Announcement surface only: toggle the bar into "replace the carousel" mode.
    _setAnnouncement(active) {
      if (this.dataset.context !== 'announcement') return;
      var bar = this.closest('.announcement-bar');
      if (bar) bar.classList.toggle('skrs-stack-active', active);
    }
  }

  function define(name, ctor) {
    if (!customElements.get(name)) customElements.define(name, ctor);
  }
  define('skrs-stack-data', SkrsStackData);
  define('skrs-stack-progress', SkrsStackProgress);
})();
